<script setup lang="ts">
  import { ScrollArea } from '@/components/ui/scroll-area'
  import { computed, nextTick, ref, watch } from 'vue'

  const props = defineProps<{
    modelValue?: string
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
  }>()

  const pad = (n: number) => String(n).padStart(2, '0')

  const selectedHour = computed(() => {
    const [h] = (props.modelValue || '00:00').split(':').map(Number)
    return h
  })

  const selectedMinute = computed(() => {
    const [, m] = (props.modelValue || '00:00').split(':').map(Number)
    return m
  })

  const hoursRef = ref<HTMLElement | null>(null)
  const minutesRef = ref<HTMLElement | null>(null)

  const selectHour = (h: number) => {
    emit('update:modelValue', `${pad(h)}:${pad(selectedMinute.value)}`)
  }

  const selectMinute = (m: number) => {
    emit('update:modelValue', `${pad(selectedHour.value)}:${pad(m)}`)
  }

  const scrollToSelected = () => {
    nextTick(() => {
      hoursRef.value?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' })
      minutesRef.value?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center' })
    })
  }

  watch(() => props.modelValue, scrollToSelected, { immediate: true })
</script>

<template>
  <div class="flex gap-0.5">
    <ScrollArea class="h-[180px] w-[52px]">
      <div
        ref="hoursRef"
        class="flex flex-col py-1"
      >
        <button
          v-for="h in 24"
          :key="h - 1"
          type="button"
          :data-selected="selectedHour === h - 1"
          class="mx-1 cursor-pointer rounded-md px-2 py-1.5 text-center text-sm transition-colors"
          :class="
            selectedHour === h - 1
              ? 'bg-purple-600 text-white'
              : 'text-gray-700 hover:bg-purple-100 hover:text-purple-700'
          "
          @click="selectHour(h - 1)"
        >
          {{ pad(h - 1) }}
        </button>
      </div>
    </ScrollArea>
    <div class="flex items-center text-sm text-gray-400">:</div>
    <ScrollArea class="h-[180px] w-[52px]">
      <div
        ref="minutesRef"
        class="flex flex-col py-1"
      >
        <button
          v-for="m in 60"
          :key="m - 1"
          type="button"
          :data-selected="selectedMinute === m - 1"
          class="mx-1 cursor-pointer rounded-md px-2 py-1.5 text-center text-sm transition-colors"
          :class="
            selectedMinute === m - 1
              ? 'bg-purple-600 text-white'
              : 'text-gray-700 hover:bg-purple-100 hover:text-purple-700'
          "
          @click="selectMinute(m - 1)"
        >
          {{ pad(m - 1) }}
        </button>
      </div>
    </ScrollArea>
  </div>
</template>
